import React, { useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";

import { money, formatDate } from "./settlementFormatters";

const inputClass =
  "w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const toInput = (value) => (value === null || value === undefined ? "" : String(value));

const MoneyField = ({ label, name, value, onChange }) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">{label}</label>
    <input
      type="number"
      min="0"
      step="0.01"
      name={name}
      value={value}
      onChange={onChange}
      placeholder="0"
      className={inputClass}
    />
  </div>
);

/**
 * Props:
 * - settlement: السجل اللي هيتعدل
 * - saving: حالة الحفظ
 * - onClose: قفل المودال
 * - onSubmit(form): بيرجع قيم الفورم للصفحة والصفحة هي اللي بتنادي الـ API
 */
const EditSettlementModal = ({ settlement, saving, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    shippingPerOrder: toInput(settlement.shippingPerOrder),
    cashAmount: toInput(settlement.cashAmount),
    instaPayAmount: toInput(settlement.instaPayAmount),
    vodafoneCashAmount: toInput(settlement.vodafoneCashAmount),
    notes: settlement.notes || "",
  });

  // قفل بزر Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [saving, onClose]);

  const paidTotal = useMemo(
    () =>
      (Number(form.cashAmount) || 0) +
      (Number(form.instaPayAmount) || 0) +
      (Number(form.vodafoneCashAmount) || 0),
    [form.cashAmount, form.instaPayAmount, form.vodafoneCashAmount]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div
      dir="rtl"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-blue-100 bg-blue-50 p-5">
          <div>
            <h2 className="text-lg font-bold text-blue-800">تعديل التسوية</h2>
            <p className="mt-1 text-sm text-blue-700">
              {settlement.representativeName} - {formatDate(settlement.settlementDate)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            title="إغلاق"
            className="rounded-lg p-2 text-blue-700 transition hover:bg-blue-100 disabled:opacity-50"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <MoneyField
              label="الشحن لكل أوردر"
              name="shippingPerOrder"
              value={form.shippingPerOrder}
              onChange={handleChange}
            />
            <MoneyField label="كاش" name="cashAmount" value={form.cashAmount} onChange={handleChange} />
            <MoneyField label="InstaPay" name="instaPayAmount" value={form.instaPayAmount} onChange={handleChange} />
            <MoneyField
              label="Vodafone Cash"
              name="vodafoneCashAmount"
              value={form.vodafoneCashAmount}
              onChange={handleChange}
            />

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-bold text-slate-700">ملاحظات</label>
              <textarea
                name="notes"
                rows="3"
                value={form.notes}
                onChange={handleChange}
                placeholder="اكتب ملاحظات التسوية..."
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>

          {/* Live totals */}
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-blue-50 p-4">
              <p className="text-sm font-bold text-blue-800">صافي المطلوب</p>
              <p className="mt-1 text-2xl font-black text-blue-700">{money(settlement.netRequiredAmount)} ج.م</p>
            </div>

            <div className="rounded-xl bg-green-50 p-4">
              <p className="text-sm font-bold text-green-800">إجمالي المدفوع بعد التعديل</p>
              <p className="mt-1 text-2xl font-black text-green-700">{money(paidTotal)} ج.م</p>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            النتيجة النهائية (المتبقي / المستحق) هتظهر بعد الحفظ لأن النظام هو اللي بيحسبها.
          </p>

          {/* Actions */}
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              إلغاء
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 px-8 py-3 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "جاري حفظ التعديل..." : "حفظ التعديل"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSettlementModal;